// The account track's shared pieces (wave 12): the art wall behind sign-in and first run, account API helpers, the
// archetype and genre labels, and the account pages' styles (scoped under .acct, injected once, so style.css stays
// the shell's). Moves to src/zones/account/ when the zone framework lands.

import { api } from "../api.ts";
import { getCatalogue, type Game } from "../catalogue.ts";
import { h } from "../dom.ts";
import { coverUrl } from "../rows.ts";
import { loadMe, type Me } from "../session.ts";

// ---- account API -----------------------------------------------------------------------------------------------

export interface Taste {
  archetypes: string[];
  genres: string[];
}
export interface Notify {
  gameReleased: boolean;
  gameUpdated: boolean;
  /** wave 12, once the backend track writes them; absent on older accounts */
  creatorShipped?: boolean;
  eventStarting?: boolean;
  /** wave 13 (w13-notify, web/site/src/notify.ts Prefs); absent on an older accounts */
  eventReminder?: boolean;
  friendMessages?: boolean;
  friends?: boolean;
  noteShipped?: boolean;
  followReleases?: boolean;
  replies?: boolean;
  gameNews?: boolean;
  forks?: boolean;
  quiet?: { from: string; to: string } | null;
  asked?: string[];
}
export type Audience = "everyone" | "friends" | "nobody";
export interface Privacy {
  profile: Audience;
  library: Audience;
  playtime: Audience;
  presence: Audience;
  friendRequests: "everyone" | "friends_of_friends" | "nobody";
}
export interface Consent {
  analytics: boolean;
  crashReports: boolean;
  playtime: boolean;
}
/** Me as accounts sends it from wave 12 (older builds leave the new fields out). */
export interface FullMe extends Me {
  privacy: Privacy;
  consent: Consent;
  taste?: Taste;
  notify?: Notify;
  terms?: { version: string | null; acceptedAt: number | null; current: string };
  onboardedAt?: number | null;
}

export async function patchMe(body: Record<string, unknown>): Promise<FullMe> {
  const me = await api<FullMe>("accounts", "/me", { method: "PATCH", body });
  await loadMe(true);
  return me;
}

export const getFullMe = () => api<FullMe>("accounts", "/me");

/** First run still to do: a new account, or one from before wave 12 that never saw it. */
export const needsFirstRun = (m: Partial<FullMe> | null): boolean => !!m && "onboardedAt" in m && m.onboardedAt === null;

// ---- taste -----------------------------------------------------------------------------------------------------

/** The curators' archetypes (WAVE12-WORLDS.md), as accounts validates them, with the words a player picks by. */
export const ARCHETYPES: { id: string; name: string; line: string; hue: number }[] = [
  { id: "speed-garage", name: "Speed Garage", line: "Racing, drifting, anything with wheels", hue: 14 },
  { id: "trigger", name: "Trigger", line: "Shooters and competitive play", hue: 352 },
  { id: "cozy-street", name: "Cozy Street", line: "Slow days, small towns, street life", hue: 32 },
  { id: "puzzle-mind", name: "Puzzle Mind", line: "Brain teasers and clever twists", hue: 190 },
  { id: "thrill-seeker", name: "Thrill Seeker", line: "Horror, spooky, jump scares", hue: 24 },
  { id: "explorer", name: "Explorer", line: "Open worlds and discovery", hue: 150 },
  { id: "story-lover", name: "Story Lover", line: "Worlds with something to say", hue: 280 },
  { id: "builder", name: "Builder", line: "Sandboxes and toys", hue: 48 },
  { id: "strategist", name: "Strategist", line: "Plans, tactics, the long game", hue: 210 },
  { id: "party", name: "Party", line: "Drop-in games with friends", hue: 320 },
  { id: "idler", name: "Idler", line: "Casual and incremental", hue: 96 },
  { id: "sky-space", name: "Sky & Space", line: "Flight and the stars", hue: 230 },
  { id: "arcade-retro", name: "Arcade Retro", line: "Pixels, high scores, one more go", hue: 300 },
  { id: "art-vibes", name: "Art & Vibes", line: "Experiences, art and music", hue: 260 },
  { id: "tinkerer", name: "Tinkerer", line: "Tech demos and how things work", hue: 170 },
];
export const MAX_ARCHETYPES = 5;

/** Game genres a player can pick (accounts' taxonomy); labels as the catalogue shows them. */
export const GENRES: [string, string][] = [
  ["action", "Action"], ["adventure", "Adventure"], ["driving", "Driving"], ["racing", "Racing"], ["flight", "Flight"],
  ["shooter", "Shooter"], ["sports", "Sports"], ["puzzle", "Puzzle"], ["platformer", "Platformer"], ["rpg", "RPG"],
  ["strategy", "Strategy"], ["simulation", "Simulation"], ["casual", "Casual"], ["horror", "Horror"], ["arcade", "Arcade"],
  ["storytelling", "Stories"], ["toy", "Toys"], ["art", "Art"], ["music", "Music"],
];
export const MAX_GENRES = 8;

// ---- the art wall ----------------------------------------------------------------------------------------------

/** Games with art, in a stable shuffle per day so the wall changes but doesn't jump between pages. */
export function wallGames(games: Game[], n: number, seed = new Date().toISOString().slice(0, 10)): Game[] {
  let x = [...seed].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  const rnd = () => ((x = (x * 1664525 + 1013904223) >>> 0) / 2 ** 32);
  const withArt = games.filter((g) => coverUrl(g, "thumb") && g.kind !== "example" && g.kind !== "learn");
  const out = [...withArt];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out.slice(0, n);
}

/**
 * Three bands of cover art drifting past each other, behind everything. It paints as an empty dark stage at once
 * and fills when the catalogue arrives; under reduced motion the bands stand still.
 */
export function artWall(tone: "night" | "dawn" = "night"): HTMLElement {
  const bands = [0, 1, 2].map((i) => h("div", { class: `wall-band b${i}` }));
  const wall = h("div", { class: `acct-wall ${tone}`, "aria-hidden": "true" }, h("div", { class: "wall-tilt" }, bands), h("div", { class: "wall-veil" }));
  void getCatalogue()
    .then((cat) => {
      const games = wallGames(cat.games, 30);
      if (games.length < 6) return;
      bands.forEach((band, i) => {
        const mine = games.filter((_, j) => j % 3 === i);
        // each band holds its tiles twice so the drift loops without a seam
        for (const g of [...mine, ...mine]) {
          const tile = h("div", { class: "wall-tile" });
          tile.style.backgroundImage = `url("${coverUrl(g, "thumb")}")`;
          band.appendChild(tile);
        }
      });
      requestAnimationFrame(() => wall.classList.add("on"));
    })
    .catch(() => {});
  return wall;
}

// ---- styles ----------------------------------------------------------------------------------------------------

let styled = false;

/** Adds the account pages' styles once. */
export function accountStyles(): void {
  if (styled || typeof document === "undefined") return;
  styled = true;
  document.head.appendChild(h("style", { id: "acct-style" }, CSS));
}

const CSS = `
/* account pages get no games search (WAVE12-WORLDS: the global chrome fits the zone) */
body[data-route="signin"] .topbar, body[data-route="welcome"] .topbar, body[data-route="me"] .topbar, body[data-route="u"] .topbar { display: none; }
/* the art wall is fixed behind the page: keep the footer above it */
body[data-route="signin"] #foot, body[data-route="welcome"] #foot { position: relative; z-index: 1; }
.acct { --a-ink: #f6f1ea; --a-dim: #b7afc0; --a-faint: #7f778b; --a-glass: rgba(20, 16, 28, .72); --a-line: rgba(255, 255, 255, .10);
  --a-hot: #ff3d7f; --a-warm: #ff8a3d; --a-sun: #ffd23d; --a-ok: #6fd39a;
  position: relative; color: var(--a-ink); }
.acct h1, .acct h2, .acct h3 { color: var(--a-ink); letter-spacing: -0.02em; }
.acct .kicker { font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--a-warm); margin: 0 0 10px; }
.acct .lede { color: var(--a-dim); font-size: 17px; line-height: 1.5; margin: 0; }
.acct .dim { color: var(--a-dim); }
.acct .hot { background: linear-gradient(90deg, var(--a-hot), var(--a-warm) 60%, var(--a-sun)); -webkit-background-clip: text; background-clip: text; color: transparent; }
.acct .btn { border-radius: 999px; }
.acct .btn.wide { width: 100%; justify-content: center; text-align: center; }
.acct-link { background: none; border: 0; padding: 0; color: var(--a-dim); text-decoration: underline; text-underline-offset: 3px; cursor: pointer; font: inherit; }
.acct-link:hover { color: var(--a-ink); }

/* the art wall */
.acct-wall { position: fixed; inset: 0; z-index: 0; overflow: hidden; background: radial-gradient(120% 90% at 70% 10%, #2a1630 0%, #120d18 55%, #0b0910 100%); pointer-events: none; }
.acct-wall.dawn { background: radial-gradient(120% 90% at 30% 0%, #3a1a22 0%, #150f18 60%, #0b0910 100%); }
.wall-tilt { position: absolute; inset: -20% -30%; display: flex; flex-direction: column; justify-content: center; gap: 18px; transform: rotate(-8deg); opacity: 0; transition: opacity 1.4s ease; }
.acct-wall.on .wall-tilt { opacity: .55; }
.wall-band { display: flex; gap: 18px; width: max-content; animation: wall-drift 120s linear infinite; }
.wall-band.b1 { animation-direction: reverse; animation-duration: 150s; }
.wall-band.b2 { animation-duration: 100s; }
.wall-tile { width: clamp(180px, 22vw, 320px); aspect-ratio: 16 / 9; border-radius: 14px; background: #241d2c center / cover no-repeat; box-shadow: 0 20px 50px rgba(0,0,0,.45); flex: none; }
@keyframes wall-drift { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.wall-veil { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(11,9,16,.96) 0%, rgba(11,9,16,.82) 38%, rgba(11,9,16,.35) 75%, rgba(11,9,16,.6) 100%), linear-gradient(0deg, rgba(11,9,16,.9), transparent 40%); }
@media (max-width: 760px) { .wall-veil { background: linear-gradient(180deg, rgba(11,9,16,.55) 0%, rgba(11,9,16,.92) 45%, rgba(11,9,16,.98) 100%); } .acct-wall.on .wall-tilt { opacity: .7; } }
@media (prefers-reduced-motion: reduce) { .wall-band { animation: none; } .wall-tilt { transition: none; } }

/* sign-in */
.acct-signin { position: relative; z-index: 1; min-height: calc(100vh - 140px); display: grid; align-items: center; padding: 40px 0 64px; }
.acct-signin-in { display: grid; grid-template-columns: minmax(0, 480px) 1fr; gap: 48px; align-items: center; }
.acct-hero h1 { font-size: clamp(40px, 5.2vw, 72px); line-height: .98; margin: 0 0 18px; font-weight: 850; }
.acct-card { background: var(--a-glass); -webkit-backdrop-filter: blur(18px) saturate(1.2); backdrop-filter: blur(18px) saturate(1.2); border: 1px solid var(--a-line); border-radius: 22px; padding: 28px; box-shadow: 0 30px 80px rgba(0,0,0,.45); margin-top: 28px; }
.acct-card h2 { font-size: 24px; margin: 0 0 8px; }
.acct-card p { margin: 0 0 12px; line-height: 1.5; }
.acct-card .field { margin-bottom: 14px; }
.acct-card label { display: block; font-size: 13px; font-weight: 600; color: var(--a-dim); margin-bottom: 6px; }
.acct-card input[type=email], .acct-card input[type=date], .acct-card input[type=text], .acct-card input:not([type]) { width: 100%; box-sizing: border-box; font-size: 16px; padding: 13px 14px; border-radius: 12px; background: rgba(255,255,255,.06); border: 1px solid var(--a-line); color: var(--a-ink); }
.acct-card input:focus { outline: 2px solid var(--a-hot); outline-offset: 1px; }
.acct-card .fine { font-size: 13px; color: var(--a-faint); line-height: 1.5; margin: 12px 0 0; }
.acct-card .note { background: rgba(255,255,255,.05); border: 1px solid var(--a-line); border-radius: 12px; padding: 12px 14px; margin: 12px 0; }
.acct-card .error { color: #ff9a9a; }
.acct-methods { display: grid; gap: 10px; margin: 6px 0 4px; }
.acct-methods:empty { display: none; }
.acct-provider { display: flex; align-items: center; justify-content: center; gap: 10px; font-weight: 650; }
.acct-provider.discord { background: #5865f2; border-color: #5865f2; color: #fff; }
.acct-provider.github { background: #f6f1ea; border-color: #f6f1ea; color: #141016; }
.acct-provider-mark { font-size: 15px; line-height: 1; }
.acct-or { display: flex; align-items: center; gap: 12px; color: var(--a-faint); font-size: 13px; margin: 8px 0 2px; }
.acct-or::before, .acct-or::after { content: ""; flex: 1; height: 1px; background: var(--a-line); }
.acct-state { display: grid; grid-template-columns: 56px 1fr; gap: 16px; align-items: start; }
.acct-glyph { width: 56px; height: 56px; border-radius: 16px; display: grid; place-items: center; font-size: 26px; background: rgba(255,255,255,.07); border: 1px solid var(--a-line); }
.acct-glyph.go { background: linear-gradient(135deg, var(--a-hot), var(--a-warm)); border: 0; }
.acct-glyph.mail { animation: mail-bob 2.6s ease-in-out infinite; }
@keyframes mail-bob { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-6px) rotate(3deg); } }
@media (prefers-reduced-motion: reduce) { .acct-glyph.mail { animation: none; } }
.acct-row { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-top: 16px; }
.acct-steps { display: flex; gap: 6px; margin: 0 0 18px; padding: 0; list-style: none; }
.acct-steps li { height: 4px; flex: 1; border-radius: 4px; background: rgba(255,255,255,.12); }
.acct-steps li.on { background: linear-gradient(90deg, var(--a-hot), var(--a-warm)); }
.acct-perks { list-style: none; padding: 0; margin: 24px 0 0; display: grid; gap: 12px; }
.acct-perks li { display: flex; gap: 12px; align-items: baseline; color: var(--a-dim); font-size: 15px; }
.acct-perks b { color: var(--a-ink); font-weight: 650; }
.acct-perks li::before { content: ""; flex: none; width: 8px; height: 8px; border-radius: 50%; background: var(--a-warm); transform: translateY(-1px); }
.acct-enter { animation: acct-in .5s cubic-bezier(.2,.8,.2,1) both; }
@keyframes acct-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .acct-enter { animation: none; } }
@media (max-width: 860px) { .acct-signin-in { grid-template-columns: 1fr; gap: 0; } .acct-signin { align-items: start; padding-top: 24px; } .acct-hero h1 { font-size: 40px; } .acct-perks { display: none; } .acct-card { padding: 22px; } }

/* first run */
.acct-welcome { position: relative; z-index: 1; max-width: 880px; margin: 0 auto; padding: 32px 0 80px; }
.acct-welcome h1 { font-size: clamp(32px, 4vw, 52px); line-height: 1.02; margin: 0 0 12px; font-weight: 850; }
.acct-picks { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin: 20px 0; }
.acct-pick { position: relative; text-align: left; padding: 16px; border-radius: 16px; border: 1px solid var(--a-line); background: linear-gradient(150deg, hsla(var(--h), 80%, 55%, .22), hsla(var(--h), 70%, 30%, .10)), rgba(20,16,28,.7); color: var(--a-ink); cursor: pointer; font: inherit; transition: transform .15s ease, border-color .15s ease, background .15s ease; min-height: 92px; }
.acct-pick:hover { transform: translateY(-2px); border-color: rgba(255,255,255,.25); }
.acct-pick b { display: block; font-size: 16px; margin-bottom: 4px; }
.acct-pick span { color: var(--a-dim); font-size: 13px; line-height: 1.4; }
.acct-pick[aria-pressed=true] { border-color: hsl(var(--h), 90%, 65%); background: linear-gradient(150deg, hsla(var(--h), 85%, 55%, .45), hsla(var(--h), 70%, 35%, .25)), rgba(20,16,28,.7); }
.acct-pick[aria-pressed=true]::after { content: "✓"; position: absolute; top: 12px; right: 14px; font-weight: 800; color: hsl(var(--h), 95%, 75%); }
.acct-pick:disabled { opacity: .4; cursor: not-allowed; transform: none; }
.acct-chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 0; }
a.acct-chip { text-decoration: none; }
.acct-chip { padding: 9px 14px; border-radius: 999px; border: 1px solid var(--a-line); background: rgba(255,255,255,.05); color: var(--a-ink); font: inherit; font-size: 14px; cursor: pointer; }
.acct-chip[aria-pressed=true] { background: var(--a-ink); color: #140f1a; border-color: var(--a-ink); font-weight: 650; }
.acct-chip:disabled { opacity: .4; cursor: not-allowed; }
.acct-foot { position: sticky; bottom: 0; display: flex; gap: 12px; align-items: center; justify-content: space-between; padding: 16px 0; margin-top: 24px; background: linear-gradient(0deg, #0b0910 60%, transparent); }
.acct-plain { display: grid; gap: 12px; margin: 18px 0; }
.acct-plain > div { display: grid; grid-template-columns: 40px 1fr; gap: 14px; padding: 16px; border-radius: 16px; background: var(--a-glass); border: 1px solid var(--a-line); }
.acct-plain .ic { width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center; background: rgba(255,255,255,.07); font-size: 18px; }
.acct-plain p { margin: 4px 0 0; color: var(--a-dim); line-height: 1.5; font-size: 15px; }
.acct-toggle { display: flex; gap: 14px; align-items: center; justify-content: space-between; padding: 16px; border-radius: 16px; background: var(--a-glass); border: 1px solid var(--a-line); margin: 12px 0; }
.acct-toggle p { margin: 4px 0 0; color: var(--a-dim); font-size: 14px; line-height: 1.45; }
.acct-switch { position: relative; flex: none; width: 50px; height: 30px; border-radius: 999px; border: 0; background: rgba(255,255,255,.18); cursor: pointer; transition: background .2s; }
.acct-switch::after { content: ""; position: absolute; top: 3px; left: 3px; width: 24px; height: 24px; border-radius: 50%; background: #fff; transition: transform .2s; }
.acct-switch[aria-checked=true] { background: var(--a-ok); }
.acct-switch[aria-checked=true]::after { transform: translateX(20px); }
@media (prefers-reduced-motion: reduce) { .acct-switch, .acct-switch::after, .acct-pick { transition: none; } }
.acct-people { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; margin: 18px 0; }
.acct-person { display: grid; grid-template-columns: 56px 1fr auto; gap: 12px; align-items: center; padding: 12px; border-radius: 16px; background: var(--a-glass); border: 1px solid var(--a-line); }
.acct-person .face { width: 56px; height: 56px; border-radius: 14px; background: #2a2333 center / cover; }
.acct-person b { display: block; font-size: 15px; }
.acct-person span { font-size: 13px; color: var(--a-dim); }
.acct-check { display: flex; gap: 12px; align-items: flex-start; padding: 14px 16px; border-radius: 14px; border: 1px solid var(--a-line); background: rgba(255,255,255,.04); cursor: pointer; }
.acct-check input { width: 20px; height: 20px; margin-top: 2px; accent-color: var(--a-hot); flex: none; }
@media (max-width: 600px) { .acct-picks { grid-template-columns: 1fr 1fr; gap: 10px; } .acct-pick { padding: 12px; min-height: 84px; } .acct-pick span { display: none; } .acct-foot { flex-direction: column-reverse; align-items: stretch; } .acct-foot .btn { width: 100%; text-align: center; justify-content: center; } }
/* account pages (home, settings, profile) */
.acct-page { padding: 28px 0 80px; }
.set-head h1 { font-size: clamp(30px, 3.4vw, 44px); margin: 0 0 20px; font-weight: 850; }
.set-grid { display: grid; grid-template-columns: 200px minmax(0, 760px); gap: 40px; align-items: start; }
.set-nav { position: sticky; top: 90px; display: grid; gap: 2px; }
.set-nav a { color: var(--a-dim); text-decoration: none; padding: 9px 12px; border-radius: 10px; font-weight: 550; }
.set-nav a:hover { color: var(--a-ink); background: rgba(255,255,255,.05); }
.set-card { background: var(--panel, #18181d); border: 1px solid var(--a-line); border-radius: 20px; padding: 24px; margin-bottom: 20px; scroll-margin-top: 90px; }
.set-card h2 { font-size: 20px; margin: 0 0 6px; }
.set-card > p.dim { margin: 0 0 8px; }
.set-row { display: flex; gap: 20px; align-items: center; justify-content: space-between; padding: 16px 0; border-top: 1px solid var(--a-line); }
.set-row:first-of-type { border-top: 0; }
.set-row > div:first-child { min-width: 0; }
.set-row b { font-weight: 650; }
.set-row p { margin: 4px 0 0; color: var(--a-dim); font-size: 14px; line-height: 1.45; overflow-wrap: anywhere; }
.set-select { flex: none; background: rgba(255,255,255,.06); color: var(--a-ink); border: 1px solid var(--a-line); border-radius: 10px; padding: 8px 10px; font: inherit; }
.set-form { display: grid; grid-template-columns: 1fr 1fr auto; gap: 12px; align-items: end; padding-bottom: 8px; }
.set-form .field { margin: 0; }
.set-form label, .set-danger label { display: block; font-size: 13px; font-weight: 600; color: var(--a-dim); margin-bottom: 6px; }
.set-form input, .set-danger input, .set-card input[type=text] { width: 100%; box-sizing: border-box; padding: 10px 12px; border-radius: 10px; background: rgba(255,255,255,.06); border: 1px solid var(--a-line); color: var(--a-ink); font: inherit; }
.set-form > div[aria-live] { grid-column: 1 / -1; }
.set-form .btn { justify-self: start; }
.set-danger { margin-top: 16px; border: 1px solid rgba(255,120,120,.3); border-radius: 14px; padding: 14px 16px; }
.set-danger summary { cursor: pointer; color: #ff9a9a; font-weight: 650; }
.set-danger p { color: var(--a-dim); line-height: 1.5; }
.set-danger .field { margin: 10px 0; max-width: 320px; }
.btn.danger { background: #c8364f; border-color: #c8364f; color: #fff; }
.set-steps { padding-left: 20px; line-height: 1.6; color: var(--a-dim); }
.set-steps code { color: var(--a-ink); background: rgba(255,255,255,.07); padding: 2px 6px; border-radius: 6px; }
@media (max-width: 860px) { .set-grid { grid-template-columns: 1fr; gap: 16px; } .set-nav { position: static; display: flex; overflow-x: auto; gap: 4px; padding-bottom: 4px; } .set-nav a { white-space: nowrap; background: rgba(255,255,255,.05); border-radius: 999px; padding: 8px 14px; font-size: 14px; } .set-form { grid-template-columns: 1fr; } .set-card { padding: 18px; border-radius: 16px; } .set-row { flex-wrap: wrap; gap: 10px; } }

/* your space and profiles */
.acct-hello { position: relative; overflow: hidden; border-radius: 24px; min-height: 280px; display: flex; align-items: flex-end; margin-bottom: 28px; background: linear-gradient(135deg, #2a1630, #141019); border: 1px solid var(--a-line); }
.acct-hello-bg { position: absolute; inset: 0; background: center 30% / cover no-repeat; opacity: .55; transform: scale(1.04); animation: hello-pan 30s ease-in-out infinite alternate; }
.acct-hello::after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(11,9,16,.92) 0%, rgba(11,9,16,.6) 55%, rgba(11,9,16,.2) 100%), linear-gradient(0deg, rgba(11,9,16,.85), transparent 60%); }
@keyframes hello-pan { from { transform: scale(1.04) translateX(0); } to { transform: scale(1.1) translateX(-2%); } }
@media (prefers-reduced-motion: reduce) { .acct-hello-bg { animation: none; } }
.acct-hello-in { position: relative; z-index: 1; padding: 32px; max-width: 640px; }
.acct-hello h1 { font-size: clamp(32px, 4vw, 54px); line-height: 1; margin: 0 0 10px; font-weight: 850; }
/* the site's one avatar language: a circle in the ruby-to-violet gradient (style.css .avatar-badge), at hero size */
.acct-face { width: 84px; height: 84px; border-radius: 50%; display: grid; place-items: center; font-size: 38px; font-weight: 850; color: #fff; background: linear-gradient(140deg, var(--ruby), #7a2bff); margin-bottom: 16px; box-shadow: 0 12px 40px rgba(122,43,255,.35); }
/* a public profile sits in the same 1120 px column as the other people pages */
.acct-page.profile-page { max-width: 1120px; margin: 0 auto; }
.acct-mods { display: grid; gap: 28px; }
.acct-mod { min-width: 0; }
.acct-mod-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.acct-mod-head h2 { font-size: 22px; margin: 0; }
.acct-strip { list-style: none; margin: 0; padding: 0 0 8px; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(220px, 280px); gap: 16px; overflow-x: auto; scroll-snap-type: x mandatory; }
.acct-strip > li { scroll-snap-align: start; }
.acct-empty { background: var(--a-glass); border: 1px dashed rgba(255,255,255,.18); border-radius: 20px; padding: 28px; }
.acct-empty h2 { margin: 0 0 8px; }
.acct-notes { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.acct-note a { display: grid; grid-template-columns: 64px 1fr auto; gap: 14px; align-items: center; padding: 10px 14px 10px 10px; border-radius: 14px; background: var(--panel, #18181d); border: 1px solid var(--a-line); color: var(--a-ink); text-decoration: none; }
.acct-note a:hover { border-color: rgba(255,255,255,.22); }
.acct-note.unread a { border-color: rgba(255,61,127,.45); background: linear-gradient(90deg, rgba(255,61,127,.10), transparent 60%), var(--panel, #18181d); }
.acct-note-art { width: 64px; aspect-ratio: 16 / 9; border-radius: 8px; background: #2a2333 center / cover; }
.acct-note b { display: block; font-weight: 650; }
.acct-note .dim { display: block; font-size: 13px; margin-top: 2px; }
.acct-note time { font-size: 13px; white-space: nowrap; }
.acct-quest { display: flex; gap: 20px; align-items: center; justify-content: space-between; padding: 24px; border-radius: 20px; background: radial-gradient(120% 140% at 0% 0%, rgba(255,122,26,.25), transparent 60%), #140d12; border: 1px solid rgba(255,122,26,.35); }
.acct-quest h2 { margin: 0 0 4px; }
.acct-pumpkins { list-style: none; display: flex; gap: 10px; padding: 0; margin: 12px 0 0; font-size: 30px; }
.acct-pumpkins li { filter: grayscale(1) brightness(.5); transition: filter .4s; }
.acct-pumpkins li.lit { filter: drop-shadow(0 0 10px rgba(255,140,40,.8)); }
@media (max-width: 760px) { .acct-hello { min-height: 240px; border-radius: 18px; } .acct-hello-in { padding: 22px; } .acct-hello h1 { font-size: 34px; } .acct-strip { grid-auto-columns: 72%; } .acct-note a { grid-template-columns: 52px 1fr; } .acct-note-art { width: 52px; } .acct-note time { grid-column: 2; } .acct-quest { flex-direction: column; align-items: flex-start; } }
`;
